// Structured logging that goes to the console and, when enabled, to Dash0.

import { reportError, sendEvent } from "@dash0/sdk-web";
import { telemetryEnabled } from "@/telemetry";

type LogAttributes = Record<string, string | number | boolean | undefined>;

type Level = "debug" | "info" | "warn" | "error";

const DASH0_SEVERITIES = {
    debug: "DEBUG",
    error: "ERROR",
    info: "INFO",
    warn: "WARN",
} as const;

function toTelemetryAttributes(attributes: LogAttributes) {
    return Object.fromEntries(
        Object.entries(attributes).filter((entry): entry is [string, string | number | boolean] => (
            entry[1] !== undefined
        )),
    );
}

function writeToConsole(level: Level, message: string, attributes: LogAttributes) {
    const context = toTelemetryAttributes(attributes);
    const suffix = Object.keys(context).length > 0 ? ` ${JSON.stringify(context)}` : "";

    console[level](`${new Date().toISOString()} ${level.toUpperCase().padEnd(5)} ${message}${suffix}`);
}

function write(level: Exclude<Level, "error">, message: string, attributes: LogAttributes = {}) {
    const context = toTelemetryAttributes(attributes);

    writeToConsole(level, message, context);

    if (telemetryEnabled) {
        sendEvent("onevault.log", {
            title: message,
            data: message,
            attributes: context,
            severity: DASH0_SEVERITIES[level],
        });
    }
}

/**
 * Flattens an unknown thrown value into exception attributes, so failures are
 * searchable by type and message rather than only by their text.
 */
function exceptionAttributes(error: unknown): LogAttributes {
    if (error instanceof Error) {
        return {
            "exception.message": error.message,
            "exception.stacktrace": error.stack,
            "exception.type": error.name,
        };
    }

    return {
        "exception.message": typeof error === "string" ? error : JSON.stringify(error),
        "exception.type": typeof error,
    };
}

export const logger = {
    debug: (message: string, attributes?: LogAttributes) => {
        write("debug", message, attributes);
    },
    /** Reports an exception to Dash0 so it appears in Website Monitoring's error view. */
    error: (message: string, error?: unknown, attributes?: LogAttributes) => {
        const details = error === undefined ? {} : exceptionAttributes(error);

        writeToConsole("error", message, { ...attributes, ...details });

        if (telemetryEnabled) {
            reportError(error instanceof Error ? error : new Error(message), {
                attributes: toTelemetryAttributes({
                    ...attributes,
                    "onevault.log.message": message,
                    ...(error instanceof Error ? {} : details),
                }),
            });
        }
    },
    info: (message: string, attributes?: LogAttributes) => {
        write("info", message, attributes);
    },
    warn: (message: string, error?: unknown, attributes?: LogAttributes) => {
        write("warn", message, {
            ...attributes,
            ...(error === undefined ? {} : exceptionAttributes(error)),
        });
    },
};
